

import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Typography,
  Button,
  Paper,
  Radio,
  Stack,
  CircularProgress,
  TextField,
  Collapse,
  Chip,
  Divider,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PaymentIcon from "@mui/icons-material/Payment";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CloseIcon from "@mui/icons-material/Close";
import { apiRequest } from '../../fetchSetup';

import CustomDialog from "../../CustomDialog";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import axios from 'axios';

const paymentOptions = [
  { 
    id: "online", 
    label: "Pay Online", 
    type: "online", 
    icon: <PaymentIcon sx={{ color: "#008080" }} />,
    description: "UPI, Cards, Net Banking, Wallets"
  },
  { 
    id: "offline", 
    label: "Pay at Clinic", 
    type: "offline", 
    icon: <LocalHospitalIcon sx={{ color: "#008080" }} />,
    description: "Pay during your visit"
  },
];

const PaymentMethodSelection = () => {
  const [selected, setSelected] = useState("online");
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogType, setDialogType] = useState("info");

  // Promo code states
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [showAvailablePromos, setShowAvailablePromos] = useState(false);
  const [availablePromos, setAvailablePromos] = useState([]);

  // Subscription states
  const [subscriptionBenefits, setSubscriptionBenefits] = useState(null);
  const [subscriptionDiscount, setSubscriptionDiscount] = useState(0);
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  // API endpoints
  const bookAppointmentApi = process.env.REACT_APP_BOOK_APPOINTMENT_API;
  const verifyPaymentApi = process.env.REACT_APP_PROCESS_PAYEMENT;
  const razorpaycreationApi = process.env.REACT_APP_CREATE_RAZORPAY_ORDER;
  const razorpaykeyApi = process.env.REACT_APP_RAZORPAY_KEY;

  // Get booking data from location state
  const { name, dob, gender, selectedDate, serviceTitle, prescription, 
    servicePrice, phone, orderType, diagnosticOrderData,deliveryAddress,appliedPromoData
  } = location.state || {};

  // Helper function to show dialog
  const showDialog = (message, type = "info") => {
    setDialogMessage(message);
    setDialogType(type);
    setDialogOpen(true);
  };

  // Validate data on mount
  useEffect(() => {
    if (!name || !selectedDate || !serviceTitle || !servicePrice) {
      showDialog("Missing booking information. Please start from the beginning.", "error");
      setTimeout(() => navigate(-1), 2000);
    }
  }, [name, selectedDate, serviceTitle, servicePrice, navigate]);

  const clearBookingFormData = () => {
    try {
      sessionStorage.removeItem("commonFormData");
      console.log(" Booking form data cleared successfully");
    } catch (error) {
      console.error("Error clearing form data:", error);
    }
  };

  // Fetch subscription benefits
  useEffect(() => {
    const fetchSubscriptionBenefits = async () => {
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      
      if (!userId || !token) return;

      const apiBaseUrl = process.env.REACT_APP_PRICING_API;
      if (!apiBaseUrl) {
        console.error("REACT_APP_PRICING_API is not configured");
        return;
      }

      setLoadingSubscription(true);
      try {
        const url = `${apiBaseUrl}/check-benefits/${userId}`;
        const response = await apiRequest(url, {
          method: "GET",
          headers: {
            
            "Content-Type": "application/json"
          }
        });

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          console.error("API returned non-JSON response");
          return;
        }

        if (!response.ok) return;

        const data = await response.json();
        if (data.success && data.has_subscription) {
          setSubscriptionBenefits(data);
        }
      } catch (error) {
        console.error("Error fetching subscription benefits:", error);
      } finally {
        setLoadingSubscription(false);
      }
    };

    fetchSubscriptionBenefits();
  }, []);

  // Calculate subscription discount
  useEffect(() => {
    if (subscriptionBenefits && subscriptionBenefits.has_subscription) {
      let discountPercent = 0;
      
      if (orderType === "diagnostic") {
        discountPercent = subscriptionBenefits.benefits.diagnostic_discount || 0;
      } else {
        discountPercent = subscriptionBenefits.benefits.service_discount || 0;
      }
      
      const discountAmount = (servicePrice * discountPercent) / 100;
      setSubscriptionDiscount(discountAmount);
    } else {
      setSubscriptionDiscount(0);
    }
  }, [servicePrice, subscriptionBenefits, orderType]);

  // Fetch available promo codes
  useEffect(() => {
    fetchAvailablePromos();
  }, [orderType]);

  const fetchAvailablePromos = async () => {
    try {
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      
      const serviceType = orderType === "diagnostic" ? "diagnostic" : "service";
      
      const response = await apiRequest(
        `${process.env.REACT_APP_APPLY_PROMOTION_API}/available?service_type=${serviceType}&user_id=${userId}`,
        {
          headers: {
            
            "Content-Type": "application/json"
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAvailablePromos(data.promotions || []);
        }
      }
    } catch (error) {
      console.error("Error fetching promos:", error);
    }
  };

  const applyPromoCode = async (code) => {
    setPromoLoading(true);
    try {
      const userId = sessionStorage.getItem("userId");
      const token = sessionStorage.getItem("token");
      
      const serviceType = orderType === "diagnostic" ? "diagnostic" : "service";
      
      const response = await apiRequest(
        `${process.env.REACT_APP_APPLY_PROMOTION_API}/apply`,
        {
          method: "POST",
          headers: {
            
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            promo_code: code.toUpperCase(),
            service_type: serviceType,
            order_price: servicePrice,
            user_id: userId,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setAppliedPromo({
          code: data.promo_code,
          discount: data.discount_amount,
          title: data.title,
        });
        setPromoCode("");
        setShowAvailablePromos(false);
        showDialog(data.message || "Promo code applied successfully!", "success");
      } else {
        showDialog(data.error || "Invalid promo code", "error");
      }
    } catch (error) {
      console.error("Error applying promo:", error);
      showDialog("Failed to apply promo code. Please try again.", "error");
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromoCode = () => {
    setAppliedPromo(null);
    showDialog("Promo code removed", "info");
  };

  const applySubscriptionToOrder = async () => {
    if (!subscriptionBenefits || !subscriptionBenefits.has_subscription) {
      return null;
    }

    const userId = sessionStorage.getItem("userId");
    const token = sessionStorage.getItem("token");
    
    const apiBaseUrl = process.env.REACT_APP_PRICING_API;
    if (!apiBaseUrl) return null;

    try {
      const url = `${apiBaseUrl}/apply-subscription`;
      const serviceType = orderType === "diagnostic" ? "diagnostic" : "service";
      
      const response = await apiRequest(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          user_id: userId,
          service_type: serviceType,
          base_price: servicePrice
        })
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        return null;
      }

      if (!response.ok) return null;

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error applying subscription:", error);
      return null;
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = process.env.REACT_APP_RAZORPAY_SCRIPT;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const createBooking = async (paymentData) => {
    const storedUser = JSON.parse(sessionStorage.getItem("user")) || {};

    const cleanPaymentData = {
      ...paymentData,
      amount: typeof paymentData.amount === 'string' 
        ? parseFloat(paymentData.amount.toString().replace(/[^0-9.]/g, ''))
        : paymentData.amount
    };

    // Apply subscription to backend
    const subscriptionResponse = await applySubscriptionToOrder();
    const diagnosticTestsString =
    orderType === "diagnostic"
      ? diagnosticOrderData?.tests
          ?.map(t => t.test_name)
          .join(", ")
      : "";
  
    const bookingPayload = {
      name,
      email: storedUser.email,
      phone: phone || storedUser.phone,
      gender,
      age: dob,
      service_type: serviceTitle,
      date: selectedDate,
      diagnostic_tests: diagnosticTestsString,
      notes: orderType === "diagnostic"
        ? "Booked through diagnostic services"
        : "Booked through the services page",

      // notes: orderType === "diagnostic" 
      //   ? `Diagnostic tests: ${diagnosticOrderData?.tests?.map(t => t.test_name).join(', ')}` 
      //   : "Booked through the services page",
      source: "webApp",
      order_from:"Booked by Resident Web",
      payment: cleanPaymentData,
      prescription,
      // ✅ Always include discount fields (even if 0)
      subscription_discount: subscriptionDiscount || 0,
      promo_discount: diagnosticOrderData.promo_discount ||0,
      promo_code: diagnosticOrderData.promo_code || null,
      subscription_id: subscriptionBenefits?.subscription?.subscription_id || null,
      delivery_address: orderType === "diagnostic" ? deliveryAddress : null,
      ...(orderType === "diagnostic" && {
        order_type: "diagnostic",
        // diagnostic_tests: diagnosticOrderData?.tests || [],
        test_details: diagnosticOrderData,
      }),
    };

    try {
      const res = await apiRequest(bookAppointmentApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(bookingPayload),
      });

      const result = await res.json();
      console.log(result)
     console.log(appliedPromoData) 
      const appointmentOrderData={
          service_type:"diagnostic",
          order_id: result.booking_id,
          items: bookingPayload.test_details.tests,
          patient_details: bookingPayload,
          promoDiscount:bookingPayload.promo_discount ||0,
          subtotal: result.amount,
          discount: result.total_savings,
          total_amount: result.amount,
          appliedPromo:appliedPromoData,
          payment: { method: result.payment_method },
          created_at: new Date().toISOString()
        }

        try{
            const invoice_response = await axios.post(
              `${process.env.REACT_APP_API_INVOICE}/invoice/pos_invoice`,
              appointmentOrderData,
            );
      
          const invoice = invoice_response.data.invoice_details;
          if (invoice.storage_type === "s3") {
            // Open CloudFront URL
            window.open(invoice.cloudfront_url, "_blank");
          } else if (invoice.storage_type === "local") {
            try {
            const token = sessionStorage.getItem("token");

            const response = await axios.get(
              `${process.env.REACT_APP_API_INVOICE}/diagnostic-invoice/download/${appointmentOrderData.order_id}`,
              {
                responseType: "blob",
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);

            window.open(url, "_blank");

          } catch (error) {
            console.error("Invoice download failed:", error);
          }
          }

        }
        catch (error) {
            console.error("Invoice upload failed:", error);
          }
      
      if (!res.ok) {
        throw new Error(result.error || result.detail || "Booking failed");
      }

      // CRITICAL: Clear form data after successful booking
      clearBookingFormData();
      
      navigate("/booking-status", {
        state: { 
          ...result, 
          name, 
          serviceTitle, 
          selectedDate,
          paymentMethod: cleanPaymentData.method
        },
      });

    } catch (err) {
      console.error("❌ Booking error:", err);
      throw err;
    }
  };

  const handleOfflinePayment = async () => {
    const paymentData = {
      payment_id: `offline_${Date.now()}`,
      amount: finalAmount,
      method: "offline",
      status: "pending",
      gateway: null,
      transaction_history: [
        {
          timestamp: new Date().toISOString(),
          method: "offline",
          status: "pending",
        }
      ],
    };
  
    await createBooking(paymentData);
  };

  const handleOnlinePayment = async () => {
    const storedUser = JSON.parse(sessionStorage.getItem("user")) || {};

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Failed to load Razorpay SDK. Please check your internet connection.");
      }

      const orderRes = await apiRequest(razorpaycreationApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          amount: finalAmount,
          currency: "INR",
          receipt: `receipt_${Date.now()}`,
          notes: {
            name: name,
            service: serviceTitle,
            date: selectedDate
          }
        }),
      });

      if (!orderRes.ok) {
        const errorData = await orderRes.json();
        throw new Error(errorData.error || errorData.detail || "Failed to create payment order");
      }

      const orderData = await orderRes.json();

      const options = {
        key: razorpaykeyApi,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Ayuja Services",
        description: `Appointment: ${serviceTitle}`,
        order_id: orderData.id,
        
        prefill: {
          name: storedUser.name || name,
          email: storedUser.email,
          contact: storedUser.phone || phone,
        },
        
        config: {
          display: {
            blocks: {
              banks: {
                name: "All Payment Methods",
                instruments: [
                  { method: "upi", flows: ["qr", "collect", "intent"] },
                  { method: "card" },
                  { method: "netbanking" },
                  { method: "wallet" },
                ],
              },
            },
            sequence: ["block.banks"],
            preferences: { show_default_blocks: true },
          },
        },
        
        theme: { 
          color: "#008080",
          backdrop_color: "rgba(0, 0, 0, 0.5)",
        },
        
        handler: async function (response) {
          await handlePaymentSuccess(response, orderData);
        },
        
        modal: {
          ondismiss: function() {
            showDialog("Payment cancelled. Please try again when ready.", "warning");
            setLoading(false);
          },
          confirm_close: true,
          escape: true,
        },
        
        retry: {
          enabled: true,
          max_count: 3,
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error("💥 Online payment error:", err);
      showDialog(err.message || "Payment initialization failed. Please try again.", "error");
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (razorpayResponse, orderData) => {
    try {
      const verifyRes = await apiRequest(verifyPaymentApi, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature,
          method: "online",
          amount: orderData.amount,
        }),
      });

      if (!verifyRes.ok) {
        const errorData = await verifyRes.json();
        throw new Error(errorData.error || errorData.detail || "Payment verification failed");
      }

      const verifyData = await verifyRes.json();

      if (verifyData.status !== "success") {
        throw new Error(
          verifyData.message || `Payment verification failed. Please contact support with Payment ID: ${razorpayResponse.razorpay_payment_id}`
        );
      }

      const paymentData = {
        payment_id: razorpayResponse.razorpay_payment_id,
        order_id: razorpayResponse.razorpay_order_id,
        method: "online",
        status: "success",
        amount: orderData.amount,
        gateway: "razorpay",
        currency: "INR",
        transaction_history: [
          {
            timestamp: new Date().toISOString(),
            method: "online",
            gateway: "razorpay",
            payment_id: razorpayResponse.razorpay_payment_id,
            order_id: razorpayResponse.razorpay_order_id,
            status: "success",
          }
        ],
      };

      await createBooking(paymentData);

    } catch (err) {
      console.error("💥 Payment verification error:", err);
      showDialog(err.message || "Payment verification failed. Please contact support.", "error");
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    if (!selected) {
      showDialog("Please select a payment method", "warning");
      return;
    }

    setLoading(true);

    try {
      if (selected === "offline") {
        await handleOfflinePayment();
      } else if (selected === "online") {
        await handleOnlinePayment();
      }
    } catch (err) {
      console.error("Error in handleContinue:", err);
      showDialog("An error occurred. Please try again.", "error");
      setLoading(false);
    }
  };

  const promoDiscount = appliedPromo ? appliedPromo.discount : 0;
  const finalAmount = Math.max(0, servicePrice - subscriptionDiscount - promoDiscount);

  return (
    <>
      <Box
        sx={{
          p: 4,
          maxWidth: 600,
          mx: "auto",
          backgroundColor: "#fff",
          mt: 2,
          border: "2px solid #008080",
          borderRadius: 2,
        }}
      >
        <IconButton onClick={() => navigate(-1)} disabled={loading}>
          <ArrowBackIosNewIcon />
        </IconButton>

        <Typography
          variant="h5"
          sx={{ mb: 2, color: "#004d4d", fontWeight: 700 }}
        >
          PAYMENT METHOD
        </Typography>

        <Typography variant="body1" sx={{ mb: 3, color: "#555" }}>
          Choose how you'd like to pay
        </Typography>


{/* ── Booking Summary ── */}
<Paper
  elevation={0}
  sx={{mb: 3,borderRadius: 3,border: "1px solid #E0F2F1",overflow: "hidden",}}
>
  {/* Header bar */}
  <Box
    sx={{px: 2.5,py: 1.5,bgcolor: "#006D77",display: "flex",alignItems: "center",gap: 1,
    }}
  >
    <CheckCircleOutlineIcon sx={{ color: "#fff", fontSize: 18 }} />
    <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 700, letterSpacing: 0.5 }}>
      Booking Summary
    </Typography>
  </Box>

  <Box sx={{ px: 2.5, py: 2 }}>
    {/* Service name */}
    <Typography variant="body2" sx={{ color: "#374151", mb: 0.5 }}>
      <strong>Service:</strong> {serviceTitle}
    </Typography>
    <Typography variant="body2" sx={{ color: "#374151", mb: 1.5 }}>
      <strong>Date:</strong>{" "}
      {selectedDate ? new Date(selectedDate).toLocaleString() : "N/A"}
    </Typography>










{orderType === "diagnostic" && diagnosticOrderData?.tests?.length > 0 && (
  <Box
    sx={{ bgcolor: "#F8FFFE", border: "1px solid #B2DFDB", borderRadius: 2, mb: 2, overflow: "hidden" }}
  >
    {/* Table header */}
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr auto auto",
        px: 2, py: 1,
        bgcolor: "#E0F7FA",
        borderBottom: "1px solid #B2DFDB",
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#00695C" }}>
        Test Name
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#00695C", textAlign: "right", mr: 3 }}>
        MRP
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#00695C", textAlign: "right" }}>
         Price
      </Typography>
    </Box>

    {/* Test rows */}

{diagnosticOrderData.tests.map((test, index) => {
  const memberPrice = (test.non_membership && test.non_membership > 0)
  ? test.non_membership
  : test.proposed_rate ?? test.mrp;
  const qty = test.quantity || 1;
  const hasDiscount = test.mrp !== memberPrice;

  return (
       <Box
          key={index}
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr auto auto",
            alignItems: "center",
            px: 2, py: 1.2,
            borderBottom: index < diagnosticOrderData.tests.length - 1 ? "1px dashed #E0E0E0" : "none",
            "&:hover": { bgcolor: "#F0FDFC" },
          }}
        >
      {/* Test Name */}
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "#1F2937", display: "block" }}>
          {test.test_name}{qty > 1 ? ` × ${qty}` : ""}
        </Typography>
        {test.test_code && (
          <Typography variant="caption" sx={{ color: "#9CA3AF", fontSize: 10 }}>
            {test.test_code}
          </Typography>
        )}
      </Box>

      {/* MRP — always struck through */}
      <Typography variant="caption" sx={{ mr: 3, color: "#9CA3AF", textDecoration: "line-through", fontWeight: 400, whiteSpace: "nowrap" }}>
        ₹{(test.mrp * qty).toFixed(2)}
      </Typography>

      {/* Member Price */}
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#006D77", whiteSpace: "nowrap" }}>
     ₹{(memberPrice * qty)?.toFixed(2)} 
      </Typography>
    </Box>
  );
})}



    {/* Savings badge */}
    {diagnosticOrderData.savings > 0 && (
      <Box
        sx={{
          px: 2, py: 1,
          bgcolor: "#ECFDF5",
          borderTop: "1px solid #B2DFDB",
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <SavingsOutlinedIcon sx={{ fontSize: 14, color: "#059669" }} />
        <Typography variant="caption" sx={{ color: "#059669", fontWeight: 600 }}>
          Test discount: ₹{diagnosticOrderData.savings.toFixed(2)} saved
        </Typography>
      </Box>
    )}
  </Box>
)}


















    {/* ── Price Breakdown ── */}
    <Box sx={{ mt: 1 }}>
      {/* Base amount */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}>
        <Typography variant="body2" sx={{ color: "#6B7280" }}>
          Base Amount
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, color: "#374151" }}>
          ₹{servicePrice?.toFixed(2)}
        </Typography>
      </Box>

      {/* Subscription discount */}
      {subscriptionBenefits?.has_subscription && subscriptionDiscount > 0 && (
        <Box
          sx={{display: "flex",justifyContent: "space-between",alignItems: "flex-start",mb: 0.8,bgcolor: "#F5F3FF",border: "1px solid #DDD6FE",borderRadius: 1.5,px: 1.5,py: 1,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: "#7C3AED", fontWeight: 700, display: "block" }}>
              🎉 Subscription Discount (
              {orderType === "diagnostic"
                ? subscriptionBenefits.benefits.diagnostic_discount
                : subscriptionBenefits.benefits.service_discount}
              %)
            </Typography>
            <Typography variant="caption" sx={{ color: "#8B5CF6" }}>
              {subscriptionBenefits.subscription.name}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "#7C3AED", fontWeight: 700 }}>
            −₹{subscriptionDiscount.toFixed(2)}
          </Typography>
        </Box>
      )}

      {/* Promo discount */}
      {appliedPromo && (
        <Box
          sx={{display: "flex",justifyContent: "space-between",alignItems: "center",mb: 0.8,bgcolor: "#ECFDF5",border: "1px solid #A7F3D0",borderRadius: 1.5,px: 1.5,py: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
            <LocalOfferIcon sx={{ fontSize: 14, color: "#059669" }} />
            <Typography variant="caption" sx={{ color: "#065F46", fontWeight: 700 }}>
              Coupon: {appliedPromo.code}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "#059669", fontWeight: 700 }}>
            −₹{appliedPromo.discount.toFixed(2)}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 1.5, borderStyle: "dashed" }} />

      {/* Total */}
      <Box
        sx={{display: "flex",justifyContent: "space-between",alignItems: "center",border: "2px solid #006D77",borderRadius: 2,px: 2,py: 1.2,
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 700, color: "#004D40" }}>
          Total Payable
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 800, color: "#006D77" }}>
          ₹{finalAmount.toFixed(2)}
        </Typography>
      </Box>

      {/* Total savings pill */}
      {(subscriptionDiscount > 0 || promoDiscount > 0 || (diagnosticOrderData?.savings ?? 0) > 0) && (
        <Box
          sx={{mt: 1.2,display: "flex",justifyContent: "center",alignItems: "center",gap: 0.6,bgcolor: "#ECFDF5",borderRadius: 10,py: 0.6,px: 2,}}
        >
          <SavingsOutlinedIcon sx={{ fontSize: 15, color: "#059669" }} />
          <Typography variant="caption" sx={{ color: "#059669", fontWeight: 700 }}>
            You are saving ₹
            {(
              subscriptionDiscount +
              promoDiscount +
              (diagnosticOrderData?.savings ?? 0)
            ).toFixed(2)}{" "}
            on this order!
          </Typography>
        </Box>
      )}
    </Box>
  </Box>
</Paper>


        {/* Payment Options */}
        <Stack spacing={2}>
          {paymentOptions.map((option) => {
            const isSelected = selected === option.id;
            return (
              <Paper
                key={option.id}
                variant="outlined"
                onClick={() => !loading && setSelected(option.id)}
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderColor: isSelected ? "#008080" : "#ccc",
                  backgroundColor: isSelected ? "#E6F7F7" : "#fff",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.6 : 1,
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: loading ? "#ccc" : "#008080",
                    backgroundColor: loading ? (isSelected ? "#E6F7F7" : "#fff") : "#E6F7F7",
                  }
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  {option.icon}
                  <Box>
                    <Typography fontWeight={isSelected ? 600 : 400}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.description}
                    </Typography>
                  </Box>
                </Stack>
                <Radio
                  checked={isSelected}
                  value={option.id}
                  disabled={loading}
                  onChange={() => setSelected(option.id)}
                  sx={{
                    color: "#008080",
                    "&.Mui-checked": { color: "#008080" },
                  }}
                />
              </Paper>
            );
          })}
        </Stack>



        <Box display="flex" justifyContent="center" mt={4}>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#008080",
              "&:hover": { backgroundColor: "#006666" },
              px: 4,
              minWidth: 200,
            }}
            onClick={handleContinue}
            disabled={loading}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ color: "#fff", mr: 1 }} />
                Processing...
              </>
            ) : (
              "Proceed to Pay"
            )}
          </Button>
        </Box>
      </Box>

      <CustomDialog
        open={dialogOpen}
        message={dialogMessage}
        onClose={() => setDialogOpen(false)}
      />


    </>
  );
};

export default PaymentMethodSelection;